import React from 'react';
import { Formik, Form as FormikForm } from 'formik';
import * as Yup from 'yup';
import { Card, Button, App as AntdApp } from 'antd';
import { PasswordField } from '../components/form/fields/PasswordField';
import logoImage from '../assets/logo.png';
import { themeConfig } from '../layout/theme'; 
import { resetPassword } from '../api/accountsApi';
import { useNavigate } from 'react-router-dom';
import { clearAuthSession, isAuthenticated } from '../utils/auth';
import { navigateBack } from '../hooks/useBackNavigation';

const resetPasswordSchema = Yup.object().shape({
  oldPassword: Yup.string().required('Old password is required'),
  newPassword: Yup.string()
    .min(6, 'Password must be at least 6 characters')
    .required('New password is required')
    .test('not-same-as-old', 'New password must be different from old password', function (value) {
      const { oldPassword } = this.parent;
      return value && oldPassword && value !== oldPassword;
    }),
  confirmPassword: Yup.string()
    .oneOf([Yup.ref('newPassword')], 'Passwords must match')
    .required('Confirm password is required'),
});

const ResetPassword: React.FC = () => {
  const { message } = AntdApp.useApp(); 
  const navigate = useNavigate();
  const isAuth = isAuthenticated();

  const handleSubmit = async (values: { oldPassword: string; newPassword: string; confirmPassword: string }) => {
    try {
      const payload = {
        oldPassword: btoa(values.oldPassword.trim()),
        newPassword: btoa(values.newPassword.trim()),
      };

      const res = await resetPassword(payload);
      // Check if the response is successful              
      message.success(res.data.message || 'Password reset successfully.');

      // Redirect based on `isAuth`
      if (isAuth) { 
        navigateBack(navigate);
      } else {
        clearAuthSession();
        navigate('/login'); // Redirect to login page
      }
    } catch (error: any) {
      const err = error?.message || 'Failed to reset password';
      message.error(err); // Highlight error using toast
    }
  };

  const renderAuthenticatedView = () => (
    <div className="w-full flex items-center justify-center">
      <div className="w-1/2 p-10 border border-gray-300 rounded-lg bg-white">
        <h1 className="text-center text-2xl font-bold text-gray-700 mb-6">Change Your Password</h1>
        <div className="w-full">
          {renderForm()}
        </div>
      </div>
    </div>
  );

  const renderForm = () => (
    <Formik
      initialValues={{ oldPassword: '', newPassword: '', confirmPassword: '' }}
      validationSchema={resetPasswordSchema}
      onSubmit={handleSubmit}
    >
      {({ isSubmitting }) => (
        <FormikForm
          autoComplete="off"
          className="px-4 pb-6 mx-auto w-full max-w-md" // Fixed width for form fields
        >
          <div className="space-y-4">
            <PasswordField name="oldPassword" label="Old Password" placeholder="Enter old password" />
            <PasswordField name="newPassword" label="New Password" placeholder="Enter new password" />
            <PasswordField name="confirmPassword" label="Confirm Password" placeholder="Confirm new password" />
          </div>
          <div className="mt-6">
            <Button
              type="primary"
              htmlType="submit"
              className="w-full"
              style={{
                backgroundColor: themeConfig.token.colorPrimary,
                borderColor: themeConfig.token.colorPrimary,
                color: '#fff',
              }}
              loading={isSubmitting}
            >
              {isAuth ? 'Change' : 'Reset'} Password
            </Button>
          </div>
        </FormikForm>
      )}
    </Formik>
  );

  const renderUnauthenticatedView = () => (
    <div className="p-6 flex items-center justify-center w-screen h-screen">
      <Card className="w-full max-w-sm shadow-xl rounded-2xl bg-white">
        <div className="flex flex-col items-center justify-center">
          <img
            src={logoImage}
            alt="Logo"
            className="w-32 h-auto object-contain mt-6 mb-2"
          />
          <h2 className="text-center text-lg font-medium text-gray-500 mb-6">Reset your password</h2>
        </div>
        {renderForm()}
      </Card>
    </div>
  );

  return (
    <div
      className={`flex items-center justify-center ${isAuth ? 'h-full w-full' : 'w-screen h-screen bg-gray-100'
        }`}
    >
      {isAuth ? renderAuthenticatedView() : renderUnauthenticatedView()}
    </div>
  );
};

export default ResetPassword;
